import React from 'react';
import { Calendar, DollarSign, Users, FileText, AlertTriangle } from 'lucide-react';
import { Deal } from '../../types';

interface DealDetailsProps {
  deal: Deal;
  onClose: () => void;
}

export default function DealDetails({ deal, onClose }: DealDetailsProps) {
  const riskLevel = deal.value > 10000000 ? 'High' : deal.value > 5000000 ? 'Medium' : 'Low';
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-25 flex items-center justify-center">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-3xl">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-medium text-gray-900">{deal.name}</h3>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-2 gap-6 mb-6">
            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Deal Overview</h4>
              <div className="space-y-3">
                <div className="flex items-center text-sm">
                  <DollarSign className="w-4 h-4 text-gray-400 mr-2" />
                  <span>
                    {new Intl.NumberFormat('en-US', {
                      style: 'currency',
                      currency: 'USD',
                      notation: 'compact'
                    }).format(deal.value)}
                  </span>
                </div>
                <div className="flex items-center text-sm">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  <span>Expected close: {new Date(deal.expectedCloseDate).toLocaleDateString()}</span>
                </div>
                <div className="flex items-center text-sm">
                  <AlertTriangle className="w-4 h-4 text-gray-400 mr-2" />
                  <span>Risk Level: {riskLevel}</span>
                </div>
              </div>
            </div>

            <div>
              <h4 className="text-sm font-medium text-gray-700 mb-2">Key Stakeholders</h4>
              <div className="space-y-2">
                {deal.team.map((member) => (
                  <div key={member.id} className="flex items-center text-sm">
                    <Users className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{member.name} - {member.role}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>

          <div className="space-y-4">
            <h4 className="text-sm font-medium text-gray-700">Recent Documents</h4>
            <div className="space-y-2">
              {deal.documents.slice(0, 3).map((doc) => (
                <div key={doc.id} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                  <div className="flex items-center text-sm">
                    <FileText className="w-4 h-4 text-gray-400 mr-2" />
                    <span>{doc.name}</span>
                  </div>
                  <span className="text-xs text-gray-500">
                    {new Date(doc.uploadDate).toLocaleDateString()}
                  </span>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 rounded-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
}